/**
 * Voice Effects Library — real-time voice changer + reverb engine (WebAudio).
 *
 * Replaces the old Dolby.io scaffolding. Runs 100% in-browser so it works in
 * live streams (Stream Video publishes the processed mic) AND in Voice Studio
 * recordings (MediaRecorder captures the processed mic). On future native
 * Android/iOS builds these presets map 1:1 to Agora's audio engine
 * (backend token service: POST /api/agora/rtc-token).
 *
 * Chain per mic stream:
 *   src → mode EQ/compressor → pitch shifter (Jungle) → ring-mod → drive
 *       → reverb (convolver) + echo (feedback delay) → mic volume → out
 *   plus an ear-monitoring tap → speakers.
 *
 * Noise suppression, echo cancellation and auto gain control are enforced via
 * getUserMedia constraints (see voiceEnhancer.js interceptor) and stay ON even
 * on weak devices where the pitch shifter is disabled.
 */

export const VOICE_PRESETS = [
  { id: "original",   label: "Original",   pitch: 0,     ring: 0,    ringHz: 50,  drive: 0,    low: 0, presence: 0 },
  { id: "deep",       label: "Deep",       pitch: -0.35, ring: 0,    ringHz: 50,  drive: 0,    low: 4, presence: 0 },
  { id: "male",       label: "Male",       pitch: -0.18, ring: 0,    ringHz: 50,  drive: 0,    low: 2, presence: 0 },
  { id: "female",     label: "Female",     pitch: 0.22,  ring: 0,    ringHz: 50,  drive: 0,    low: 0, presence: 2 },
  { id: "child",      label: "Child",      pitch: 0.45,  ring: 0,    ringHz: 50,  drive: 0,    low: 0, presence: 1 },
  { id: "robot",      label: "Robot",      pitch: 0,     ring: 0.8,  ringHz: 50,  drive: 0.15, low: 0, presence: 0 },
  { id: "monster",    label: "Monster",    pitch: -0.5,  ring: 0,    ringHz: 50,  drive: 0.5,  low: 5, presence: 0 },
  { id: "cartoon",    label: "Cartoon",    pitch: 0.6,   ring: 0,    ringHz: 50,  drive: 0,    low: 0, presence: 1 },
  { id: "electronic", label: "Electronic", pitch: 0,     ring: 0.6,  ringHz: 440, drive: 0.25, low: 0, presence: 0 },
];

export const REVERB_PRESETS = [
  { id: "none",       label: "No Reverb",  wet: 0 },
  { id: "small_room", label: "Small Room", wet: 0.25, duration: 0.35, decay: 3.5 },
  { id: "large_room", label: "Large Room", wet: 0.3,  duration: 0.9,  decay: 2.6 },
  { id: "hall",       label: "Hall",       wet: 0.35, duration: 1.8,  decay: 2.0 },
  { id: "concert",    label: "Concert",    wet: 0.4,  duration: 2.6,  decay: 1.8 },
  { id: "ktv",        label: "KTV",        wet: 0.32, duration: 1.2,  decay: 2.2, echoDelay: 0.18, echoFb: 0.3,  echoWet: 0.25 },
  { id: "studio",     label: "Studio",     wet: 0.2,  duration: 0.45, decay: 4.5 },
  { id: "echo",       label: "Echo",       wet: 0,                                echoDelay: 0.28, echoFb: 0.45, echoWet: 0.55 },
  { id: "metal",      label: "Metal",      wet: 0.45, duration: 0.8,  decay: 2.2, metallic: true },
  { id: "warm",       label: "Warm Voice", wet: 0.28, duration: 0.7,  decay: 3.0, lowpass: true },
];

export const AUDIO_MODES = [
  { id: "normal",       label: "Normal" },
  { id: "karaoke",      label: "Karaoke" },
  { id: "music",        label: "Music" },
  { id: "studio_voice", label: "Studio Voice" },
  { id: "podcast",      label: "Podcast" },
];

// Mode → EQ/compressor parameters
const MODE_PARAMS = {
  normal:       { hp: 80,  low: 0, presence: 0, compRatio: 2,  compThresh: -20 },
  karaoke:      { hp: 90,  low: 1, presence: 2, compRatio: 3,  compThresh: -22 },
  music:        { hp: 30,  low: 0, presence: 0, compRatio: 1,  compThresh: -50 }, // near-transparent
  studio_voice: { hp: 85,  low: 2, presence: 3, compRatio: 5,  compThresh: -26 },
  podcast:      { hp: 100, low: 1, presence: 3, compRatio: 4,  compThresh: -24 },
};

// ---------------------------------------------------------------- state ----
const state = {
  voice: "original",
  reverb: "none",
  mode: "normal",
  micVolume: 1.0,
  earMonitor: false,
};

const listeners = new Set();
const activeChains = new Set();
const irCache = new Map();

export function getEffectsState() {
  return { ...state };
}

export function onEffectsChange(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function notify() {
  activeChains.forEach((c) => { try { c.update(); } catch (_) {} });
  listeners.forEach((fn) => { try { fn(getEffectsState()); } catch (_) {} });
}

export function setVoicePreset(id) {
  if (VOICE_PRESETS.some((p) => p.id === id)) { state.voice = id; notify(); }
}
export function setReverbPreset(id) {
  if (REVERB_PRESETS.some((p) => p.id === id)) { state.reverb = id; notify(); }
}
export function setAudioMode(id) {
  if (AUDIO_MODES.some((m) => m.id === id)) { state.mode = id; notify(); }
}
export function setMicVolume(v) {
  state.micVolume = Math.min(2, Math.max(0, Number(v) || 0)); notify();
}
export function setEarMonitoring(on) {
  state.earMonitor = !!on; notify();
}
export function resetEffects() {
  state.voice = "original"; state.reverb = "none"; state.mode = "normal";
  state.micVolume = 1.0; state.earMonitor = false; notify();
}
export function applyFavorite(fav) {
  if (fav.voice) state.voice = fav.voice;
  if (fav.reverb) state.reverb = fav.reverb;
  if (fav.mode) state.mode = fav.mode;
  if (fav.mic_volume != null) state.micVolume = fav.mic_volume;
  notify();
}

/** Anything non-default active? Used by the getUserMedia interceptor. */
export function effectsActive() {
  return (
    state.voice !== "original" || state.reverb !== "none" ||
    state.mode !== "normal" || state.micVolume !== 1.0 || state.earMonitor
  );
}

// ------------------------------------------------------- capabilities ----
export function isEffectsSupported() {
  return !!(window.AudioContext || window.webkitAudioContext);
}

/** Weak phones: skip the pitch shifter (highest CPU cost). NS/EC stay on. */
export function isLiteDevice() {
  const cores = navigator.hardwareConcurrency || 4;
  return cores <= 2;
}

export function isPresetAvailable(id) {
  if (!isEffectsSupported()) return id === "original";
  const p = VOICE_PRESETS.find((x) => x.id === id);
  if (!p) return false;
  if (isLiteDevice() && p.pitch !== 0) return false;
  return true;
}

// --------------------------------------------- Jungle pitch shifter ----
const J_BUFFER_TIME = 0.1;
const J_FADE_TIME = 0.05;
const J_DELAY_TIME = 0.1;

function createDelayTimeBuffer(ctx, activeTime, fadeTime, shiftUp) {
  const len1 = Math.floor(activeTime * ctx.sampleRate);
  const len2 = Math.floor((activeTime - 2 * fadeTime) * ctx.sampleRate);
  const length = len1 + len2;
  const buffer = ctx.createBuffer(1, length, ctx.sampleRate);
  const p = buffer.getChannelData(0);
  for (let i = 0; i < len1; ++i) {
    p[i] = shiftUp ? (len1 - i) / len1 : i / len1;
  }
  for (let i = len1; i < length; ++i) p[i] = 0;
  return buffer;
}

function createFadeBuffer(ctx, activeTime, fadeTime) {
  const len1 = Math.floor(activeTime * ctx.sampleRate);
  const len2 = Math.floor((activeTime - 2 * fadeTime) * ctx.sampleRate);
  const length = len1 + len2;
  const buffer = ctx.createBuffer(1, length, ctx.sampleRate);
  const p = buffer.getChannelData(0);
  const fadeLength = fadeTime * ctx.sampleRate;
  const fadeIndex1 = fadeLength;
  const fadeIndex2 = len1 - fadeLength;
  for (let i = 0; i < len1; ++i) {
    if (i < fadeIndex1) p[i] = Math.sqrt(i / fadeLength);
    else if (i >= fadeIndex2) p[i] = Math.sqrt(1 - (i - fadeIndex2) / fadeLength);
    else p[i] = 1;
  }
  for (let i = len1; i < length; ++i) p[i] = 0;
  return buffer;
}

/** Real-time pitch shifter using dual modulated delay lines with crossfade. */
function createJungle(ctx) {
  const input = ctx.createGain();
  const output = ctx.createGain();

  const shiftDown = createDelayTimeBuffer(ctx, J_BUFFER_TIME, J_FADE_TIME, false);
  const shiftUp = createDelayTimeBuffer(ctx, J_BUFFER_TIME, J_FADE_TIME, true);
  const fadeBuffer = createFadeBuffer(ctx, J_BUFFER_TIME, J_FADE_TIME);

  const mod1 = ctx.createBufferSource(); mod1.buffer = shiftDown; mod1.loop = true;
  const mod2 = ctx.createBufferSource(); mod2.buffer = shiftDown; mod2.loop = true;
  const mod3 = ctx.createBufferSource(); mod3.buffer = shiftUp; mod3.loop = true;
  const mod4 = ctx.createBufferSource(); mod4.buffer = shiftUp; mod4.loop = true;

  const mod1Gain = ctx.createGain();
  const mod2Gain = ctx.createGain();
  const mod3Gain = ctx.createGain(); mod3Gain.gain.value = 0;
  const mod4Gain = ctx.createGain(); mod4Gain.gain.value = 0;
  mod1.connect(mod1Gain); mod2.connect(mod2Gain);
  mod3.connect(mod3Gain); mod4.connect(mod4Gain);

  const modGain1 = ctx.createGain();
  const modGain2 = ctx.createGain();
  const delay1 = ctx.createDelay(1);
  const delay2 = ctx.createDelay(1);
  mod1Gain.connect(modGain1); mod3Gain.connect(modGain1);
  mod2Gain.connect(modGain2); mod4Gain.connect(modGain2);
  modGain1.connect(delay1.delayTime);
  modGain2.connect(delay2.delayTime);

  const fade1 = ctx.createBufferSource(); fade1.buffer = fadeBuffer; fade1.loop = true;
  const fade2 = ctx.createBufferSource(); fade2.buffer = fadeBuffer; fade2.loop = true;
  const mix1 = ctx.createGain(); mix1.gain.value = 0;
  const mix2 = ctx.createGain(); mix2.gain.value = 0;
  fade1.connect(mix1.gain); fade2.connect(mix2.gain);

  input.connect(delay1); input.connect(delay2);
  delay1.connect(mix1); delay2.connect(mix2);
  mix1.connect(output); mix2.connect(output);

  const t = ctx.currentTime + 0.05;
  const t2 = t + J_BUFFER_TIME - J_FADE_TIME;
  mod1.start(t); mod2.start(t2); mod3.start(t); mod4.start(t2);
  fade1.start(t); fade2.start(t2);

  function setDelay(d) {
    modGain1.gain.setTargetAtTime(0.5 * d, ctx.currentTime, 0.01);
    modGain2.gain.setTargetAtTime(0.5 * d, ctx.currentTime, 0.01);
  }

  function setPitchOffset(mult) {
    if (mult > 0) {
      mod1Gain.gain.value = 0; mod2Gain.gain.value = 0;
      mod3Gain.gain.value = 1; mod4Gain.gain.value = 1;
    } else {
      mod1Gain.gain.value = 1; mod2Gain.gain.value = 1;
      mod3Gain.gain.value = 0; mod4Gain.gain.value = 0;
    }
    setDelay(J_DELAY_TIME * Math.abs(mult));
  }

  setDelay(J_DELAY_TIME);
  return { input, output, setPitchOffset };
}

// ----------------------------------------------------- reverb helpers ----
function getImpulseResponse(ctx, preset) {
  const key = `${preset.id}_${ctx.sampleRate}`;
  if (irCache.has(key)) return irCache.get(key);
  const duration = preset.duration || 1;
  const decay = preset.decay || 2;
  const length = Math.max(1, Math.floor(ctx.sampleRate * duration));
  const impulse = ctx.createBuffer(2, length, ctx.sampleRate);
  for (let ch = 0; ch < 2; ch++) {
    const data = impulse.getChannelData(ch);
    for (let i = 0; i < length; i++) {
      let v = (Math.random() * 2 - 1) * Math.pow(1 - i / length, decay);
      if (preset.metallic) {
        const period = Math.floor(ctx.sampleRate / 220);
        if (i % period < period * 0.12) v *= 3.2;
        else v *= 0.35;
      }
      data[i] = v;
    }
    if (preset.lowpass) {
      let prev = 0;
      for (let i = 0; i < length; i++) {
        prev = prev * 0.82 + data[i] * 0.18;
        data[i] = prev;
      }
    }
  }
  irCache.set(key, impulse);
  return impulse;
}

function makeDistortionCurve(amount) {
  const k = amount * 100;
  const n = 512;
  const curve = new Float32Array(n);
  for (let i = 0; i < n; ++i) {
    const x = (i * 2) / n - 1;
    curve[i] = ((3 + k) * x * 20 * (Math.PI / 180)) / (Math.PI + k * Math.abs(x));
  }
  return curve;
}

// ------------------------------------------------------- chain builder ----
/**
 * Insert the effects chain after `inputNode`. Returns { output, update }.
 * All parameters are updated live from the global state — no mic re-acquire
 * needed when the user changes presets mid-stream.
 */
export function createEffectsChain(ctx, inputNode) {
  // Mode stage
  const modeHp = ctx.createBiquadFilter();
  modeHp.type = "highpass"; modeHp.frequency.value = 80; modeHp.Q.value = 0.7;
  const modeLow = ctx.createBiquadFilter();
  modeLow.type = "lowshelf"; modeLow.frequency.value = 180; modeLow.gain.value = 0;
  const modePeak = ctx.createBiquadFilter();
  modePeak.type = "peaking"; modePeak.frequency.value = 3000; modePeak.Q.value = 1.1; modePeak.gain.value = 0;
  const modeComp = ctx.createDynamicsCompressor();
  modeComp.knee.value = 10; modeComp.attack.value = 0.004; modeComp.release.value = 0.22;

  // Pitch stage (skipped on lite devices)
  let jungle = null;
  const pitchDry = ctx.createGain();
  const pitchWet = ctx.createGain(); pitchWet.gain.value = 0;
  const pitchSum = ctx.createGain();
  if (!isLiteDevice()) {
    try { jungle = createJungle(ctx); } catch (e) { jungle = null; }
  }

  // Ring-mod stage
  const ringOsc = ctx.createOscillator();
  ringOsc.type = "sine"; ringOsc.frequency.value = 50; ringOsc.start();
  const ringNode = ctx.createGain(); ringNode.gain.value = 0;
  ringOsc.connect(ringNode.gain);
  const ringDry = ctx.createGain();
  const ringWet = ctx.createGain(); ringWet.gain.value = 0;
  const ringSum = ctx.createGain();

  // Drive stage
  const shaper = ctx.createWaveShaper();
  shaper.oversample = "2x";
  const driveDry = ctx.createGain();
  const driveWet = ctx.createGain(); driveWet.gain.value = 0;
  const driveSum = ctx.createGain();

  // Reverb + echo stage
  const convolver = ctx.createConvolver();
  const reverbWet = ctx.createGain(); reverbWet.gain.value = 0;
  const reverbDry = ctx.createGain();
  const echoDelay = ctx.createDelay(1);
  const echoFb = ctx.createGain(); echoFb.gain.value = 0;
  const echoWet = ctx.createGain(); echoWet.gain.value = 0;
  echoDelay.connect(echoFb); echoFb.connect(echoDelay);
  const spaceSum = ctx.createGain();

  // Output stage
  const micGain = ctx.createGain();
  const monitorGain = ctx.createGain(); monitorGain.gain.value = 0;

  // Wiring
  inputNode.connect(modeHp);
  modeHp.connect(modeLow); modeLow.connect(modePeak); modePeak.connect(modeComp);

  modeComp.connect(pitchDry); pitchDry.connect(pitchSum);
  if (jungle) {
    modeComp.connect(jungle.input);
    jungle.output.connect(pitchWet); pitchWet.connect(pitchSum);
  }

  pitchSum.connect(ringDry); ringDry.connect(ringSum);
  pitchSum.connect(ringNode); ringNode.connect(ringWet); ringWet.connect(ringSum);

  ringSum.connect(driveDry); driveDry.connect(driveSum);
  ringSum.connect(shaper); shaper.connect(driveWet); driveWet.connect(driveSum);

  driveSum.connect(reverbDry); reverbDry.connect(spaceSum);
  driveSum.connect(convolver); convolver.connect(reverbWet); reverbWet.connect(spaceSum);
  driveSum.connect(echoDelay); echoDelay.connect(echoWet); echoWet.connect(spaceSum);

  spaceSum.connect(micGain);
  micGain.connect(monitorGain);
  monitorGain.connect(ctx.destination);

  const T = 0.02;
  function update() {
    const now = ctx.currentTime;
    const vp = VOICE_PRESETS.find((p) => p.id === state.voice) || VOICE_PRESETS[0];
    const rp = REVERB_PRESETS.find((p) => p.id === state.reverb) || REVERB_PRESETS[0];
    const mp = MODE_PARAMS[state.mode] || MODE_PARAMS.normal;

    // Mode
    modeHp.frequency.setTargetAtTime(mp.hp, now, T);
    modeLow.gain.setTargetAtTime(mp.low + (vp.low || 0), now, T);
    modePeak.gain.setTargetAtTime(mp.presence + (vp.presence || 0), now, T);
    modeComp.ratio.setTargetAtTime(mp.compRatio, now, T);
    modeComp.threshold.setTargetAtTime(mp.compThresh, now, T);

    // Pitch
    const pitchOn = jungle && vp.pitch !== 0;
    if (pitchOn) jungle.setPitchOffset(vp.pitch);
    pitchWet.gain.setTargetAtTime(pitchOn ? 1 : 0, now, T);
    pitchDry.gain.setTargetAtTime(pitchOn ? 0 : 1, now, T);

    // Ring mod
    ringOsc.frequency.setTargetAtTime(vp.ringHz || 50, now, T);
    ringWet.gain.setTargetAtTime(vp.ring || 0, now, T);
    ringDry.gain.setTargetAtTime(1 - (vp.ring || 0) * 0.6, now, T);

    // Drive
    if (vp.drive) shaper.curve = makeDistortionCurve(vp.drive);
    driveWet.gain.setTargetAtTime(vp.drive ? 0.85 : 0, now, T);
    driveDry.gain.setTargetAtTime(vp.drive ? 0.35 : 1, now, T);

    // Reverb / echo (karaoke mode sweetens both)
    const karaokeBoost = state.mode === "karaoke" ? 1.4 : 1;
    let wet = (rp.wet || 0) * karaokeBoost;
    if (rp.wet > 0) convolver.buffer = getImpulseResponse(ctx, rp);
    reverbWet.gain.setTargetAtTime(Math.min(wet, 0.85), now, T);
    reverbDry.gain.setTargetAtTime(1 - Math.min(wet, 0.85) * 0.35, now, T);
    let eWet = rp.echoWet || 0;
    if (state.mode === "karaoke" && !eWet) eWet = 0.18;
    echoDelay.delayTime.setTargetAtTime(rp.echoDelay || 0.22, now, T);
    echoFb.gain.setTargetAtTime(eWet ? (rp.echoFb || 0.35) : 0, now, T);
    echoWet.gain.setTargetAtTime(eWet, now, T);

    // Output
    micGain.gain.setTargetAtTime(state.micVolume, now, T);
    monitorGain.gain.setTargetAtTime(state.earMonitor ? 0.7 : 0, now, T);
  }

  update();
  const chain = { output: micGain, update };
  activeChains.add(chain);
  return chain;
}

// ------------------------------------------------------- live preview ----
let previewRefs = null;

export async function startPreview() {
  if (previewRefs) return true;
  // Goes through the voiceEnhancer interceptor → returns the processed stream.
  const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
  const Ctx = window.AudioContext || window.webkitAudioContext;
  const ctx = new Ctx({ latencyHint: "interactive" });
  const src = ctx.createMediaStreamSource(stream);
  const g = ctx.createGain(); g.gain.value = 0.9;
  src.connect(g); g.connect(ctx.destination);
  previewRefs = { stream, ctx };
  return true;
}

export function stopPreview() {
  if (!previewRefs) return;
  const { stream, ctx } = previewRefs;
  stream.getTracks().forEach((t) => t.stop());
  if (stream.__rawStream) stream.__rawStream.getTracks().forEach((t) => t.stop());
  ctx.close().catch(() => {});
  previewRefs = null;
}

export function isPreviewing() {
  return !!previewRefs;
}
